/**
 * When a product cannot be ordered without the client's own photos.
 *
 * The wire says how many input images a product takes (`inputImagePolicy`) and
 * the v2 order validator enforces the same bound, so a line committed without
 * them is either rejected outright or — because `serializePlacement` drops a set
 * the BE would reject — ordered with NO images at all. Neither is visible to the
 * client at the moment they press "Add", which is the only moment they can still
 * fix it. So the drawer refuses to commit instead.
 *
 * `EXACTLY_COUNT` buys one produced item per uploaded photo (the drawer keeps
 * `quantity` equal to the upload count for exactly this reason), `UNLIMITED`
 * needs at least one, and `NONE` — or a product that takes no uploads at all —
 * needs none.
 */

import { NESTED_FLOWS } from 'utils/purchaseFlowRevamp'
import type { ConfigurationFlowId, InputImagePolicyV2 } from 'models/purchaseFlowRevamp'

/**
 * Whether the configuration step collects this product's source files, so the
 * drawer must not also offer an upload zone for them.
 *
 * The file a product is MADE from is configuration — it belongs with the layout,
 * design and template choices made FOR it, not in the drawer, whose job is
 * choosing and pricing the product.
 *
 * **The answer is now "whenever the product has a configuration page at all."**
 * Every page collects source visuals: `floor_plan_config` and `editing_config`
 * own a dropzone, and every other page is given the shared one above its module
 * (`needsSourceUploadSurface`). This was a hand-kept list of two flows and the
 * list was wrong the moment staging and floor-plan certification got their
 * surface — the live dev run found the staging drawer and the staging
 * configuration BOTH asking for the same photos, with only one of them claiming
 * them onto the line.
 *
 * A flow with no page of its own cannot collect anything, which is why the test
 * is `NESTED_FLOWS` — the same condition `withSourceUploadFlow` uses to decide a
 * product needs `editing_config` derived for it. The two read as one rule: a
 * product with no real page is GIVEN one, and a product with a real page keeps
 * its upload there.
 */
export const configurationOwnsUpload = (
  configurationFlows: ConfigurationFlowId[] | undefined
): boolean => (configurationFlows ?? []).some((flow) => !NESTED_FLOWS.has(flow))

/**
 * Whether the DRAWER itself renders a dropzone.
 *
 * One predicate for the dropzone AND the "Upload your visuals" heading above it,
 * because the heading labels the dropzone and has no meaning without it. Moving
 * the upload out and leaving the heading behind left the AI-editing drawer telling
 * the client to upload something it no longer asked for — found in the live dev
 * run, not by a test, which is why the two now share one answer.
 */
export const drawerShowsUpload = ({
  requiresUpload,
  configurationFlows,
}: {
  requiresUpload: boolean
  configurationFlows: ConfigurationFlowId[] | undefined
}): boolean =>
  requiresUpload && !configurationOwnsUpload(configurationFlows)

/**
 * The AI-editing settings a drawer commit should carry forward.
 *
 * The settings are chosen on the configuration page, so the drawer never authors
 * them — but `replaceItem` rebuilds the whole line, and a field the payload omits
 * is a field the edit deletes. So an edit carries the line's existing settings
 * through unchanged.
 *
 * Dropped when the newly selected variant does not announce `ai_editing`: they
 * would then describe a product the client is no longer buying, and
 * `serializePlacement` emits an `AI_EDITING` configuration for any line that
 * carries them.
 */
export const aiEditingToCarry = <T,>({
  isEdit,
  existing,
  variantFlows,
}: {
  isEdit: boolean
  existing: T | undefined
  variantFlows: ConfigurationFlowId[] | undefined
}): T | undefined => {
  if (!isEdit) return undefined
  if (!variantFlows?.includes('ai_editing')) return undefined
  return existing
}

/**
 * The client's brief a drawer commit should carry forward.
 *
 * Same hazard as [aiEditingToCarry]: when the configuration page owns the box the
 * drawer never authors the note, but `replaceItem` rebuilds the whole line, so a
 * payload that omits it DELETES what the client wrote. An edit therefore carries
 * the line's existing note through unchanged.
 *
 * Dropped when the newly selected variant would not offer a box at all — the note
 * then describes a product the client is no longer buying.
 */
export const noteToCarry = ({
  isEdit,
  existing,
  surface,
}: {
  isEdit: boolean
  existing: string | undefined
  surface: 'drawer' | 'configuration' | 'none'
}): string | undefined => {
  if (!isEdit) return undefined
  if (surface !== 'configuration') return undefined
  return existing?.trim() || undefined
}

export interface UploadRequirementInput {
  /** Whether the drawer is showing an upload zone for this product at all. */
  requiresUpload: boolean
  /** The SELECTED variant's policy — siblings can differ, and the committed
   *  line carries the selected one. */
  policy: InputImagePolicyV2 | undefined
  /** Files this line owns right now (other lines' files already excluded). */
  uploadedCount: number
  /** Pending quantity for the line. */
  quantity: number
}

export const isMissingRequiredUploads = ({
  requiresUpload,
  policy,
  uploadedCount,
  quantity,
}: UploadRequirementInput): boolean => {
  if (!requiresUpload) return false
  if (!policy || policy === 'NONE') return false
  if (uploadedCount === 0) return true
  // One image per produced item. Unreachable while the drawer syncs quantity to the
  // upload count, but it is the bound the order validator applies, so it is stated here.
  return policy === 'EXACTLY_COUNT' && uploadedCount !== quantity
}
